// src/components/TemplateEditor.js
// ─────────────────────────────────────────────────────────────
//  Editor for a single ModifierTemplate — name + its modifier groups.
//  Reuses GroupForm/OptionForm (the same pieces simple-item modifier
//  editing uses) so template groups look and behave identically.
//
//  If `template.id` is set, Save calls onSave with an update (existing
//  template, full-replace groups). If not, Save calls onSave to create
//  a brand new template (used by the blank "Create Template" path).
// ─────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import ConfirmModal from './ConfirmModal';
import { EMPTY_GROUP, EMPTY_OPTION, GroupForm, OptionForm, DISPLAY_TYPES } from './ModifierGroupEditor';

export default function TemplateEditor({ template, usage = [], onSave, onCancel, loading }) {
  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [name,   setName]   = useState(template?.name || '');
  const [groups, setGroups] = useState((template?.groups || []).map(g => ({ ...g })));
  const [editGroupIdx, setEditGroupIdx] = useState(null); // index into groups, or 'new'
  const [optTarget,     setOptTarget]   = useState(null); // { groupIdx, option } or { groupIdx } for new

  const saveGroup = (data) => {
    if (editGroupIdx === 'new') {
      setGroups(g => [...g, { ...data, options: [] }]);
    } else {
      setGroups(g => g.map((grp, i) => i === editGroupIdx ? { ...grp, ...data } : grp));
    }
    setEditGroupIdx(null);
  };

  const deleteGroup = (idx) => {
    setGroups(g => g.filter((_, i) => i !== idx));
  };

  const saveOption = (groupIdx, data) => {
    setGroups(g => g.map((grp, i) => {
      if (i !== groupIdx) return grp;
      const options = grp.options || [];
      if (optTarget?.option?.id !== undefined || (optTarget?.optIdx !== undefined && optTarget.optIdx !== 'new')) {
        return { ...grp, options: options.map((o, oi) => oi === optTarget.optIdx ? { ...o, ...data } : o) };
      }
      return { ...grp, options: [...options, data] };
    }));
    setOptTarget(null);
  };

  const deleteOption = (groupIdx, optIdx) => {
    setGroups(g => g.map((grp, i) => i === groupIdx ? { ...grp, options: grp.options.filter((_, oi) => oi !== optIdx) } : grp));
  };

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ id: template?.id, name: name.trim(), groups });
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:2100, overflowY:'auto', padding:24 }}>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <div style={{ background:'var(--bg2)', borderRadius:16, maxWidth:800, margin:'0 auto', border:'1px solid var(--border)' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'18px 24px', borderBottom:'1px solid var(--border)' }}>
          <div style={{ fontWeight:700, fontSize:16 }}>🗂 {template?.id ? 'Edit Template' : 'New Template'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>✕ Close</button>
        </div>

        <div style={{ padding:24 }}>
          <div style={{ marginBottom:16 }}>
            <div className="section-label" style={{ marginBottom:6 }}>Template Name *</div>
            <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Pizza Customization" />
          </div>

          {template?.sourceItemId && (
            <div style={{ fontSize:12, color:'var(--text3)', marginBottom:16 }}>
              Created from an existing menu item (copy — independent of the original item now).
            </div>
          )}

          {usage.length > 0 && (
            <div style={{ padding:'12px 16px', background:'#3d2e10', border:'1px solid var(--accent)', borderRadius:10, marginBottom:16, fontSize:13, color:'var(--accent)' }}>
              ⚠ Used by {usage.length} bundle{usage.length !== 1 ? 's' : ''}: {usage.map(u => u.name).join(', ')}.
              Saving will update all of them.
            </div>
          )}

          {groups.map((group, gi) => (
            <div key={gi} className="card" style={{ marginBottom:14, padding:0, overflow:'hidden' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', background:'var(--bg3)', borderBottom:'1px solid var(--border)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontWeight:700 }}>{group.name}</span>
                  {group.nameAr && <span style={{ fontSize:12, color:'var(--text2)' }}>{group.nameAr}</span>}
                  <span style={{ fontSize:11, padding:'2px 8px', borderRadius:10, background:'var(--bg4)', color:'var(--text2)' }}>
                    {DISPLAY_TYPES.find(d => d.id === group.displayType)?.label || group.displayType}
                  </span>
                  {group.required && <span className="badge badge-amber">Required</span>}
                </div>
                <div style={{ display:'flex', gap:6 }}>
                  <span style={{ fontSize:12, color:'var(--text2)' }}>Pick {group.minSelect}–{group.maxSelect === 99 ? '∞' : group.maxSelect}</span>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditGroupIdx(gi); setOptTarget(null); }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => setConfirm({ message: 'Remove this modifier group from the template?', confirmLabel: 'Remove', onConfirm: () => deleteGroup(gi) })}>Delete</button>
                </div>
              </div>

              <div style={{ padding:'12px 16px' }}>
                {(group.options || []).map((opt, oi) => (
                  <div key={oi} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 12px', background:'var(--bg3)', borderRadius:8, marginBottom:6 }}>
                    <div>
                      <span style={{ fontWeight:600, fontSize:13 }}>{opt.name}</span>
                      {opt.nameAr && <span style={{ fontSize:11, color:'var(--text3)', marginLeft:6 }}>{opt.nameAr}</span>}
                      {opt.isDefault && <span className="badge badge-green" style={{ marginLeft:6 }}>Default</span>}
                    </div>
                    <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                      <span style={{ fontWeight:700, color: opt.priceAdjustment > 0 ? 'var(--green)' : opt.priceAdjustment < 0 ? 'var(--red)' : 'var(--text2)', fontSize:13 }}>
                        {opt.priceAdjustment > 0 ? '+' : ''}{opt.priceAdjustment === 0 ? 'Free' : `AED ${opt.priceAdjustment}`}
                      </span>
                      <button className="btn btn-ghost btn-sm" onClick={() => setOptTarget({ groupIdx: gi, optIdx: oi, option: opt })}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteOption(gi, oi)}>✕</button>
                    </div>
                  </div>
                ))}

                {optTarget?.groupIdx === gi && optTarget.optIdx === 'new' ? (
                  <OptionForm initial={EMPTY_OPTION} onSave={(data) => saveOption(gi, data)} onCancel={() => setOptTarget(null)} loading={false} />
                ) : (
                  <button className="btn btn-ghost btn-sm" style={{ marginTop:4 }}
                    onClick={() => setOptTarget({ groupIdx: gi, optIdx: 'new' })}>
                    + Add Option
                  </button>
                )}

                {optTarget?.groupIdx === gi && optTarget.optIdx !== 'new' && optTarget.option && (
                  <div style={{ marginTop:10, padding:12, background:'var(--bg4)', borderRadius:8, border:'1px solid var(--accent)' }}>
                    <div style={{ fontWeight:600, marginBottom:10, fontSize:13 }}>Edit option: {optTarget.option.name}</div>
                    <OptionForm initial={optTarget.option} onSave={(data) => saveOption(gi, data)} onCancel={() => setOptTarget(null)} loading={false} />
                  </div>
                )}
              </div>
            </div>
          ))}

          {editGroupIdx !== null && (
            <div className="card" style={{ marginBottom:14, border:'1px solid var(--accent)' }}>
              <div style={{ fontWeight:700, marginBottom:16 }}>
                {editGroupIdx === 'new' ? 'New Modifier Group' : `Edit Group: ${groups[editGroupIdx]?.name}`}
              </div>
              <GroupForm
                initial={editGroupIdx === 'new' ? EMPTY_GROUP : groups[editGroupIdx]}
                groups={groups}
                onSave={saveGroup}
                onCancel={() => setEditGroupIdx(null)}
                loading={false}
              />
            </div>
          )}

          {editGroupIdx === null && (
            <button className="btn btn-primary" style={{ width:'100%', padding:13, marginTop:8 }}
              onClick={() => setEditGroupIdx('new')}>
              + Add Modifier Group
            </button>
          )}

          <div style={{ display:'flex', gap:8, marginTop:24, paddingTop:16, borderTop:'1px solid var(--border)' }}>
            <button className="btn btn-primary" disabled={loading || !name.trim()} onClick={handleSave}>
              {loading ? 'Saving...' : 'Save Template'}
            </button>
            <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}
